// Shared dog card generation pipeline used by the Bit Dogs game and the
// dashboard's custom breed generator. Keep prompts and processing here so
// both entry points always produce identical cards.

const PUBLIC_URL = process.env.PUBLIC_URL || "";
export const PLACEHOLDER_IMAGE = `${PUBLIC_URL}${PUBLIC_URL.endsWith("/") ? "" : "/"}placeholder.png`;

export const fetchDogDescription = async (breedName) => {
  try {
    const baseURL =
      window.location.hostname === "localhost"
        ? "http://localhost:5000"
        : "https://satoshi-homepage-59650ad0a3ce.herokuapp.com";

    const response = await fetch(`${baseURL}/api/openai/generate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        prompt: `Provide a brief 2-3 line description of the ${breedName} dog breed, including a bit about its history and characteristics. Keep it concise and engaging.`,
        type: "text",
      }),
    });

    console.log("Raw Response:", response);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("API Error Response:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log("Description API Success:", data);
    return data.result || "Unable to load description.";
  } catch (error) {
    console.error("Error in fetchDogDescription:", error);
    return "Unable to load description. Please try again later.";
  }
};

// One vivid, concrete expression per generation — image models render a
// single well-described emotion far better than a generic list of moods
const DOG_EXPRESSIONS = [
  "happily panting — mouth open in a warm grin with a bright pink tongue out, eyes gently squinted with joy",
  "curious and inquisitive — head tilted to one side, ears perked high, big wide eyes looking right at the viewer",
  "playfully excited — bouncy alert stance, mouth open in a big happy grin, tail mid-wag",
  "sweetly shy — head dipped slightly, soft warm eyes glancing up bashfully",
  "proud and confident — chest out, ears up, chin high, a pleased little smile"
];

export const fetchDogImage = async (breedName, action = null) => {
  try {
    const baseURL =
      window.location.hostname === "localhost"
        ? "http://localhost:5000"
        : "https://satoshi-homepage-59650ad0a3ce.herokuapp.com";

    const actionPrompt = action
      ? ` in mid-action: "${action}". Pose must be dynamic, readable, and full body — limbs uncropped, tail visible. Use only one small prop if essential (frisbee for "Catch Frisbee", newspaper for "Fetch Newspaper", ball for fetch). Otherwise no props. CRITICAL: wide shot, zoomed OUT — the ENTIRE dog (head, body, all four legs, and tail) fully inside the frame with generous empty blue margin on all sides. NEVER a close-up, NEVER a head-only or portrait shot, even for actions like Speak or Catch Frisbee.`
      : ` standing in a natural show pose, three-quarter view facing slightly right, ${DOG_EXPRESSIONS[Math.floor(Math.random() * DOG_EXPRESSIONS.length)]}. FULL BODY visible — head, body, all four legs, and tail all completely within the frame. No props, no ground, no grass — just the dog.`;

    const fullPrompt =
      `Detailed 16-bit retro pixel art sprite of an adorable, charismatic, beautiful ${breedName} dog${actionPrompt}` +
      ` Art style: SNES / Game Boy Advance era character sprite with the beloved-mascot quality of a classic 90s platformer hero — visibly chunky square pixels, deliberate blocky pixel clusters, hard pixel edges, crisp 1-pixel outlines, dithered shading, NO anti-aliasing, NO smooth gradients, NO soft airbrushed shading. The 16-bit sprite size gives room for real facial detail while staying unmistakably retro.` +
      ` Color palette: bright, clean, high-contrast retro-game colors that are TRUE TO THIS BREED. First identify the ${breedName}'s actual signature coat coloring — for example a West Highland White Terrier is snow WHITE, a Dalmatian is WHITE with BLACK spots, a Weimaraner is silver-GRAY, a Doberman is BLACK with rust markings, an Irish Setter is deep RED, a Golden Retriever is GOLDEN — and use exactly that coloring, then render it in vivid, punchy, saturated arcade-style versions of those true colors: brilliant snow white with cool pale-blue shading, deep inky black, rich chocolate, luminous gold, bright cream, fiery red — whichever ones THIS breed really has. Tight palette of ~5-8 colors with strong contrast between shades and crisp highlights.` +
      ` CRITICAL: DO NOT tint every dog orange, amber, gold, or brown. Do NOT apply a warm orange/sepia wash over the sprite. A white breed must read as clearly WHITE, a black breed as BLACK, a gray breed as GRAY — never orange. Also NO muddy, murky, washed-out, faded, dull, or desaturated tones anywhere. Vivid and true-to-breed, never monotone.` +
      ` THE FACE IS THE CENTERPIECE and must radiate life: large expressive eyes, each with a single bright white catchlight pixel that makes them sparkle, clean dark pupils, and readable brow pixels that shape the emotion; a well-formed snout with a crisp black nose; and a clearly drawn mouth that sells the expression at a single glance. The face must be perfectly symmetric, both eyes identical in size and shape, ears proportional and true to the breed, every feature deliberate and instantly readable — a character players fall in love with on sight, never sad, droopy, blank or lifeless, and never melted, smeared, lopsided or muddled pixels.` +
      ` Solid medium blue background (not too dark, more like a mid-tone royal/cobalt blue), no ground, no grass, no scenery, no shadow.` +
      ` CRITICAL FRAMING: The entire dog MUST be visible inside the image — head, body, ALL FOUR LEGS, and tail completely within the frame, with comfortable empty blue space around the dog on all sides. Do NOT zoom in. Do NOT crop any part of the dog. Do NOT show only the head or only the upper body. Wide shot with margin around the full dog.`;

    console.log("Making request to:", `${baseURL}/api/openai/generate`);
    console.log("Request payload prompt:", fullPrompt);

    const response = await fetch(`${baseURL}/api/openai/generate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        prompt: fullPrompt,
        type: "image",
      }),
    });

    console.log("Raw Response:", response);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("API Error Response:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log("Image API Success:", data);

    if (!data.image_url) {
      throw new Error("No image URL in response");
    }

    if (data.image_url.startsWith('data:image')) {
      return data.image_url;
    }

    return `${baseURL}/proxy-image?url=${encodeURIComponent(data.image_url)}`;
  } catch (error) {
    console.error("Error in fetchDogImage:", {
      error: error.message,
      stack: error.stack,
    });
    return PLACEHOLDER_IMAGE;
  }
};

export const removeBlueBackground = (imageUrl) => {
  return new Promise((resolve, reject) => {
    if (!imageUrl || imageUrl === PLACEHOLDER_IMAGE) {
      resolve(PLACEHOLDER_IMAGE);
      return;
    }

    // Create an offscreen canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Create an image object to load the sprite
    const img = new Image();
    img.crossOrigin = 'Anonymous';

    // Handle image load event
    img.onload = () => {
      // Set canvas dimensions
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw the image on canvas
      ctx.drawImage(img, 0, 0);

      // Get image data for processing
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const { width, height } = canvas;

      // Blue-ish test matching the generated backdrop (#1a237e family)
      const isBackgroundish = (i) => {
        if (data[i + 3] < 16) return true; // already transparent (re-runs)
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        return (b > 100 && b > r * 1.2 && b > g * 1.2) ||
          (r < 50 && g < 50 && b > 180) ||  // Bright blue
          (Math.abs(r - 26) < 30 && Math.abs(g - 35) < 30 && Math.abs(b - 126) < 30);  // Deep blue
      };

      // Remove only background CONNECTED TO THE IMAGE BORDER (flood fill).
      // A plain per-pixel color test also wiped out blue-gray COAT pixels —
      // blue-merle Aussies, Heelers, silvery breeds — leaving holes in the
      // dog. Interior pixels now stay opaque whatever their color; the one
      // exception is enclosed pockets that tightly match the exact backdrop
      // blue (e.g. a gap between the legs sealed by the outline).
      const removeMask = new Uint8Array(width * height);
      const stack = [];
      const consider = (x, y) => {
        const p = y * width + x;
        if (!removeMask[p] && isBackgroundish(p * 4)) {
          removeMask[p] = 1;
          stack.push(p);
        }
      };
      for (let x = 0; x < width; x++) { consider(x, 0); consider(x, height - 1); }
      for (let y = 0; y < height; y++) { consider(0, y); consider(width - 1, y); }
      while (stack.length) {
        const p = stack.pop();
        const px = p % width;
        const py = (p / width) | 0;
        if (px > 0) consider(px - 1, py);
        if (px < width - 1) consider(px + 1, py);
        if (py > 0) consider(px, py - 1);
        if (py < height - 1) consider(px, py + 1);
      }

      const rowCoverage = new Array(height).fill(0);
      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const p = y * width + x;
          const i = p * 4;
          const exactBackdrop =
            Math.abs(data[i] - 26) < 30 &&
            Math.abs(data[i + 1] - 35) < 30 &&
            Math.abs(data[i + 2] - 126) < 30;
          if (removeMask[p] || exactBackdrop) {
            data[i + 3] = 0; // Alpha channel (0 = fully transparent)
          }

          if (data[i + 3] !== 0) {
            rowCoverage[y] += 1;
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          }
        }
      }

      // If everything became transparent, just return the processed canvas as-is
      if (maxX === -1 || maxY === -1) {
        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL('image/png'));
        return;
      }

      const boundingHeight = maxY - minY + 1;

      // Detect palette-like horizontal band attached to the bottom of the
      // sprite. Thresholds are strict so a zoomed-in dog whose body spans
      // most of the image width is never mistaken for a palette and chopped.
      const paletteCoverageThreshold = 0.75;
      let paletteRowCount = 0;
      let paletteCoverageTotal = 0;
      let paletteTopRow = maxY;

      for (let y = maxY; y >= minY; y--) {
        const coverageRatio = rowCoverage[y] / width;

        if (coverageRatio >= paletteCoverageThreshold) {
          paletteRowCount += 1;
          paletteCoverageTotal += coverageRatio;
          paletteTopRow = y;
        } else {
          // Stop if we already started counting potential palette rows
          if (paletteRowCount > 0) {
            break;
          }
        }
      }

      const paletteAverageCoverage = paletteRowCount > 0 ? (paletteCoverageTotal / paletteRowCount) : 0;

      const paletteHeightLimit = Math.floor(height * 0.12);
      const looksLikePalette = paletteRowCount >= 2 &&
        paletteRowCount <= paletteHeightLimit &&
        paletteAverageCoverage >= 0.85 &&
        paletteRowCount <= boundingHeight * 0.3;

      if (looksLikePalette) {
        maxY = paletteTopRow - 1;
      }

      // Recalculate crop dimensions after potential palette removal
      const adjustedMaxY = Math.max(maxY, minY);
      const cropWidth = Math.max(1, maxX - minX + 1);
      const cropHeight = Math.max(1, adjustedMaxY - minY + 1);

      ctx.putImageData(imageData, 0, 0);

      if (cropWidth > 0 && cropHeight > 0) {
        const croppedCanvas = document.createElement('canvas');
        const croppedCtx = croppedCanvas.getContext('2d');
        croppedCanvas.width = cropWidth;
        croppedCanvas.height = cropHeight;

        const croppedImageData = ctx.getImageData(minX, minY, cropWidth, cropHeight);
        croppedCtx.putImageData(croppedImageData, 0, 0);

        resolve(croppedCanvas.toDataURL('image/png'));
      } else {
        resolve(canvas.toDataURL('image/png'));
      }
    };

    // Handle errors
    img.onerror = (error) => {
      console.error('Error loading image for background removal:', error);
      reject(error);
    };

    // Set source to the provided imageUrl
    img.src = imageUrl;
  });
};

// Trick/pose actions the generator can render ("give a trick")
export const ACTIONS = [
  // Basic Poses
  "Sit",
  "Lie Down",
  "Stand",          // Kept for variety
  "Shake Paw",      // Renamed from "Shake Hands"
  "Speak",          // Renamed from "Bark" for potentially clearer visual

  // Dynamic Actions from Original
  "Jump",
  "Run",
  "Catch Frisbee",
  "Roll Over",      // Kept, prompt refinement will help
  "Bow (Play Position)",

  // New Actions from User Request
  "Fetch (Ball)",   // Clarified from just "Fetch"
  "Fetch Newspaper",
  "Play Dead",
  "Spin",
  "Dig",
  "Stretch",
  "Sniff",
]; // Total: 17 Actions
